import axios from 'axios';
import Swal from 'sweetalert2';

/*
 * The one place the frontend talks to the approval transition endpoint.
 * Added 2026-10-13 together with services/mutation/Approval/PCApproveFinance.php.
 *
 * Bulk and single are the SAME call — `ids` carries one id or fifty. Five screens use this, so
 * the endpoint path, the payload shape and the way results are reported all live here rather
 * than being retyped (and drifting) in each one.
 *
 * Module keys must match the hardcoded $PC_APPROVAL_MODULES spec in that endpoint:
 *   weekly_sale | shop_payment | account_payment | pay_account | shop_transfer
 */
export const APPROVAL_MODULES = {
    WEEKLY_SALE: 'weekly_sale',
    SHOP_PAYMENT: 'shop_payment',
    ACCOUNT_PAYMENT: 'account_payment',
    PAY_ACCOUNT: 'pay_account',
    SHOP_TRANSFER: 'shop_transfer',
};

const ENDPOINT = 'services/mutation/Approval/PCApproveFinance.php';

/*
 * Post a transition. Returns the parsed body, or null if the request itself failed.
 * Shape: { success, status, message, approved, skipped: [ { id, from, reason } ] }
 */
export async function postTransition(module, to, ids) {
    const list = (Array.isArray(ids) ? ids : [ids]).map(String);
    try {
        const res = await axios.post(`${process.env.REACT_APP_BASE_URL}${ENDPOINT}`, {
            module, to, ids: list,
        });
        return res && res.data ? res.data : null;
    } catch (e) {
        return null;
    }
}

/*
 * Report the outcome.
 *
 * PARTIAL SUCCESS is a normal result here, not an edge case: the endpoint attempts every id
 * independently, so "8 approved, 2 skipped" is a perfectly ordinary answer and must not be
 * dressed up as either a clean success or a failure. Every skipped record is listed with its
 * reason, because a silent skip on a finance record is worse than a noisy one.
 */
export function reportTransition(result) {
    if (!result) {
        return Swal.fire('Something went wrong', 'The status could not be changed.', 'error');
    }

    const skipped = Array.isArray(result.skipped) ? result.skipped : [];
    const approved = Number(result.approved || 0);

    if (skipped.length === 0) {
        return approved > 0
            ? Swal.fire(result.message || 'Done', '', 'success')
            : Swal.fire('Not done', result.message || 'Nothing was changed.', 'warning');
    }

    // Group identical reasons so ten rows blocked for the same cause read as one line.
    const byReason = {};
    skipped.forEach(s => {
        const r = s.reason || 'Skipped.';
        if (!byReason[r]) byReason[r] = [];
        byReason[r].push(s.id);
    });

    const esc = (t) => String(t).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const detail = Object.keys(byReason)
        .map(r => `<li style="margin-bottom:4px"><strong>#${byReason[r].join(', #')}</strong> — ${esc(r)}</li>`)
        .join('');

    return Swal.fire({
        title: approved > 0 ? 'Partly done' : 'Nothing changed',
        icon: approved > 0 ? 'warning' : 'error',
        html: `<p style="margin-bottom:10px">${esc(result.message || '')}</p>`
            + `<ul style="text-align:left;font-size:13px;padding-left:18px;margin:0">${detail}</ul>`,
    });
}

/* Post and report in one step — what the screens normally want. Returns the result so a caller
 * can decide whether to refresh.
 *
 * `beforeReport` runs after the request completes but BEFORE the dialog opens, and exists for one
 * specific reason: helper/Loader.js is a full-screen overlay at z-index 10000, while SweetAlert2's
 * container defaults to 1060. A screen that holds the loader across `await reportTransition(...)`
 * therefore shows a dialog UNDERNEATH the overlay — visible through its 0.2 opacity, but
 * unclickable. The Swal promise never resolves, so the line that would hide the loader never runs
 * and the whole page stays blocked. (Reported 2026-10-13 on the per-record "send for approval".)
 *
 * Screens that use the global loader must pass `() => this.props.toggleLoader(false)` here rather
 * than hiding it after the await. Screens that use their own in-component busy flag (the bulk
 * bars) do not need it — a local flag disables buttons without covering the screen.
 */
export async function runTransition(module, to, ids, beforeReport) {
    const result = await postTransition(module, to, ids);
    if (typeof beforeReport === 'function') {
        beforeReport();
    }
    await reportTransition(result);
    return result;
}
